import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  AlertCircle,
  CheckCircle2,
  Users,
  Menu,
  X
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

const Calendar: React.FC = () => {
  const { colors } = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [showMobileDetails, setShowMobileDetails] = useState(false);

  const mockEvents = [
    {
      id: '1',
      title: 'React Study Session',
      date: new Date(2025, 0, 20),
      time: '10:00 AM',
      type: 'study' as const,
      duration: 120,
      description: 'Deep dive into React hooks and state management'
    },
    {
      id: '2',
      title: 'Project Deadline',
      date: new Date(2025, 0, 22),
      time: '11:59 PM',
      type: 'deadline' as const,
      duration: 0,
      description: 'Submit final project for React course'
    },
    {
      id: '3',
      title: 'Team Meeting',
      date: new Date(2025, 0, 25),
      time: '2:00 PM',
      type: 'meeting' as const,
      duration: 60,
      description: 'Weekly team sync and project updates'
    },
    {
      id: '4',
      title: 'ML Workshop',
      date: new Date(2025, 0, 28),
      time: '9:00 AM',
      type: 'study' as const,
      duration: 180,
      description: 'Machine Learning fundamentals workshop'
    }
  ];

  const getEventColor = (type: string) => {
    switch (type) {
      case 'study': return colors.blue;
      case 'deadline': return colors.red;
      case 'meeting': return colors.green;
      case 'reminder': return colors.yellow;
      default: return colors.subtext0;
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'study': return <Clock className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'deadline': return <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'meeting': return <Users className="w-3 h-3 sm:w-4 sm:h-4" />;
      case 'reminder': return <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4" />;
      default: return <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4" />;
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date: Date) => {
    return mockEvents.filter(event => 
      event.date.toDateString() === date.toDateString()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNamesShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: colors.text }}>
          Calendar
        </h1>
        
        <button 
          className="px-3 sm:px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
          style={{ backgroundColor: colors.blue }}
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Event</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Calendar Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 rounded-lg border transition-colors hover:opacity-80"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0
              }}
            >
              <ChevronLeft className="w-4 h-4" style={{ color: colors.text }} />
            </button>
            
            <h2 className="text-lg sm:text-xl font-semibold min-w-[160px] sm:min-w-[200px] text-center" style={{ color: colors.text }}>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 rounded-lg border transition-colors hover:opacity-80"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0
              }}
            >
              <ChevronRight className="w-4 h-4" style={{ color: colors.text }} />
            </button>
          </div>
          
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-2 sm:px-3 py-2 rounded-lg border transition-colors hover:opacity-80 text-sm"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            Today
          </button>
        </div>
        
        <div 
          className="flex items-center rounded-lg border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          {(['month', 'week', 'day'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-2 sm:px-3 py-2 transition-colors text-sm ${
                mode === 'month' ? 'rounded-l-lg' : mode === 'day' ? 'rounded-r-lg' : ''
              } ${viewMode === mode ? 'text-white' : ''}`}
              style={{
                backgroundColor: viewMode === mode ? colors.blue : 'transparent',
                color: viewMode === mode ? 'white' : colors.text
              }}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        <div className="flex-1">
          <div 
            className="rounded-xl border overflow-hidden"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b" style={{ borderColor: colors.surface1 }}>
              {(window.innerWidth < 640 ? dayNamesShort : dayNames).map((day, index) => (
                <div
                  key={index}
                  className="p-2 sm:p-4 text-center font-medium text-sm sm:text-base"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.text
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {days.map((day, index) => {
                const isToday = day && day.toDateString() === new Date().toDateString();
                const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
                const events = day ? getEventsForDate(day) : [];
                
                return (
                  <div
                    key={index}
                    className={`min-h-[80px] sm:min-h-[120px] p-1 sm:p-2 border-r border-b cursor-pointer transition-colors hover:opacity-80 ${
                      !day ? 'opacity-30' : ''
                    }`}
                    style={{ 
                      backgroundColor: isSelected ? colors.surface0 : colors.base,
                      borderColor: colors.surface1
                    }}
                    onClick={() => {
                      if (day) {
                        setSelectedDate(day);
                        if (window.innerWidth < 1024) {
                          setShowMobileDetails(true);
                        }
                      }
                    }}
                  >
                    {day && (
                      <>
                        <div className="flex items-center justify-between mb-1 sm:mb-2">
                          <span 
                            className={`text-xs sm:text-sm font-medium ${
                              isToday ? 'w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white' : ''
                            }`}
                            style={{ 
                              color: isToday ? 'white' : colors.text,
                              backgroundColor: isToday ? colors.blue : 'transparent'
                            }}
                          >
                            {day.getDate()}
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          {events.slice(0, window.innerWidth < 640 ? 2 : 3).map((event) => (
                            <div
                              key={event.id}
                              className="text-xs p-1 rounded truncate flex items-center gap-1"
                              style={{ 
                                backgroundColor: getEventColor(event.type) + '20',
                                color: getEventColor(event.type)
                              }}
                            >
                              {getEventIcon(event.type)}
                              <span className="truncate">{event.title}</span>
                            </div>
                          ))}
                          {events.length > (window.innerWidth < 640 ? 2 : 3) && (
                            <div 
                              className="text-xs p-1 rounded text-center"
                              style={{ 
                                backgroundColor: colors.surface0,
                                color: colors.subtext0
                              }}
                            >
                              +{events.length - (window.innerWidth < 640 ? 2 : 3)} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop Event Details Sidebar */}
        <div 
          className="hidden lg:block w-80 p-6 rounded-xl border"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
            {selectedDate ? `Events for ${selectedDate.toLocaleDateString()}` : 'Select a date'}
          </h3>
          
          {selectedDate ? (
            <div className="space-y-4">
              {getEventsForDate(selectedDate).map((event) => (
                <div
                  key={event.id}
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1
                  }}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div 
                      className="p-1 rounded"
                      style={{ backgroundColor: getEventColor(event.type) + '20' }}
                    >
                      <div style={{ color: getEventColor(event.type) }}>
                        {getEventIcon(event.type)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium" style={{ color: colors.text }}>
                        {event.title}
                      </h4>
                      <div className="flex items-center gap-2 text-sm mt-1" style={{ color: colors.subtext0 }}>
                        <Clock className="w-3 h-3" />
                        {event.time}
                        {event.duration > 0 && (
                          <span>({event.duration} min)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm" style={{ color: colors.subtext0 }}>
                    {event.description}
                  </p>
                </div>
              ))}
              
              {getEventsForDate(selectedDate).length === 0 && (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3" style={{ color: colors.subtext0 }} />
                  <p style={{ color: colors.subtext0 }}>
                    No events scheduled for this date
                  </p>
                  <button 
                    className="mt-3 px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: colors.blue }}
                  >
                    Add Event
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3" style={{ color: colors.subtext0 }} />
              <p style={{ color: colors.subtext0 }}>
                Click on a date to view events
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Event Details Modal */}
      {showMobileDetails && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 lg:hidden">
          <div 
            className="bg-white w-full max-h-[80vh] rounded-t-xl overflow-y-auto"
            style={{ backgroundColor: colors.mantle }}
          >
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: colors.surface0 }}>
              <h3 className="text-lg font-semibold" style={{ color: colors.text }}>
                {selectedDate.toLocaleDateString()}
              </h3>
              <button
                onClick={() => setShowMobileDetails(false)}
                className="p-2 rounded-lg"
                style={{ backgroundColor: colors.surface0 }}
              >
                <X className="w-4 h-4" style={{ color: colors.text }} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {getEventsForDate(selectedDate).map((event) => (
                <div
                  key={event.id}
                  className="p-4 rounded-lg border"
                  style={{ 
                    backgroundColor: colors.base,
                    borderColor: colors.surface1
                  }}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div 
                      className="p-1 rounded"
                      style={{ backgroundColor: getEventColor(event.type) + '20' }}
                    >
                      <div style={{ color: getEventColor(event.type) }}>
                        {getEventIcon(event.type)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium" style={{ color: colors.text }}>
                        {event.title}
                      </h4>
                      <div className="flex items-center gap-2 text-sm mt-1" style={{ color: colors.subtext0 }}>
                        <Clock className="w-3 h-3" />
                        {event.time}
                        {event.duration > 0 && (
                          <span>({event.duration} min)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm" style={{ color: colors.subtext0 }}>
                    {event.description}
                  </p>
                </div>
              ))}
              
              {getEventsForDate(selectedDate).length === 0 && (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3" style={{ color: colors.subtext0 }} />
                  <p style={{ color: colors.subtext0 }}>
                    No events scheduled for this date
                  </p>
                  <button 
                    className="mt-3 px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90"
                    style={{ backgroundColor: colors.blue }}
                  >
                    Add Event
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upcoming Events */}
      <div 
        className="p-4 sm:p-6 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
          Upcoming Events
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {mockEvents.slice(0, 4).map((event) => (
            <div
              key={event.id}
              className="p-4 rounded-lg border"
              style={{ 
                backgroundColor: colors.base,
                borderColor: colors.surface1
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div style={{ color: getEventColor(event.type) }}>
                  {getEventIcon(event.type)}
                </div>
                <span 
                  className="text-xs px-2 py-1 rounded"
                  style={{ 
                    backgroundColor: getEventColor(event.type) + '20',
                    color: getEventColor(event.type)
                  }}
                >
                  {event.type}
                </span>
              </div>
              
              <h4 className="font-medium mb-1" style={{ color: colors.text }}>
                {event.title}
              </h4>
              
              <div className="text-sm" style={{ color: colors.subtext0 }}>
                {event.date.toLocaleDateString()} at {event.time}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Calendar;